import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Bell } from 'lucide-react';
import { Link } from 'react-router-dom';
import { errorMessage } from '../../services/errors';
import { webPush } from '../../services/webPush';
import { useRefreshWebPushStatus, useWebPushStatus } from '../../services/webPushStatus';
import { outlineButton, primaryButton } from '../../components/styles';

/**
 * A browser this account has not turned notifications on in is asked once, on
 * Today, so a signed-in account has a device that can receive reminders before
 * one is due rather than after one has already failed (CER-150). The Android
 * app registers itself at sign-in; a browser can only be asked.
 *
 * It is a line on the page, never a modal, and never on first paint: it waits
 * until the person has captured something, so a browser that has only ever
 * seen the sign-in page is not asked. It does not wait for a reminder — by the
 * time one is queued it is too late to ask.
 *
 * Saying "Not now" is remembered in this browser and the invitation does not
 * come back; Settings shows the state and turns notifications on at any time.
 * Saying no to the browser's own prompt blocks the site, which Settings reads
 * and explains how to undo.
 */
const DISMISSED_KEY = 'notification-invite-dismissed';

const wasDismissed = (): boolean => {
  try {
    return localStorage.getItem(DISMISSED_KEY) === 'yes';
  } catch {
    // A browser that keeps nothing asks again next visit rather than never.
    return false;
  }
};

const rememberDismissed = () => {
  try {
    localStorage.setItem(DISMISSED_KEY, 'yes');
  } catch {
    // Nothing kept: the invitation is still gone for this visit.
  }
};

export default function NotificationInvite({ used }: { used: boolean }) {
  const [dismissed, setDismissed] = useState(wasDismissed);
  const status = useWebPushStatus();
  const refresh = useRefreshWebPushStatus();
  const turnOn = useMutation({ mutationFn: () => webPush.turnOn(), onSettled: refresh });

  // Nothing to ask for: the person has not used the app here yet, they have
  // said no, or this browser is already on, already blocked, or cannot.
  if (!used || dismissed || status.data !== 'off') return null;

  const sayNo = () => {
    rememberDismissed();
    setDismissed(true);
  };

  return (
    <div role="region" aria-label="Notifications" className="flex flex-wrap items-center gap-3 rounded-xl border border-primary/30 bg-primary/5 p-4 text-sm">
      <Bell className="w-5 h-5 shrink-0 text-primary" />
      {/* A width of its own to start from: with none, the buttons beside it leave it a sliver
          on a phone and the sentence stands one letter to a line. Below that width it wraps onto its own row. */}
      <p className="min-w-0 flex-1 basis-64">
        Let this browser receive your reminders and daily digest. You can change it any time in{' '}
        <Link to="/settings" className="font-medium underline">
          Settings
        </Link>
        .
      </p>
      <button type="button" onClick={() => turnOn.mutate()} disabled={turnOn.isPending} className={primaryButton}>
        {turnOn.isPending ? 'Turning on…' : 'Turn on notifications'}
      </button>
      <button type="button" onClick={sayNo} disabled={turnOn.isPending} className={outlineButton}>
        Not now
      </button>
      {turnOn.isError && <p className="w-full text-error">Not turned on: {errorMessage(turnOn.error)}</p>}
    </div>
  );
}
